import { useEffect, useState } from "react";
import {
  ApiFehler,
  bestellungBestaetigen,
  otpAnfordern,
  speisekarte,
  type BestellungAnsicht,
  type ReviewAnsicht,
  type SpeiseAnsicht,
} from "../api";
import { Speisekarte } from "./Speisekarte";
import { WarenkorbListe } from "./WarenkorbListe";
import { Kasse, type KassenEingabe } from "./Kasse";
import { OtpBestaetigung } from "./OtpBestaetigung";
import { Bestaetigung } from "./Bestaetigung";
import { neueZeile, zuBestellzeilen, type Warenkorbzeile } from "./warenkorb";

type Phase = "einkauf" | "code" | "bestaetigt";

/** Der Kundenladen: Speisekarte → Warenkorb → Kasse → OTP → Bestätigung. */
export function Storefront() {
  const [speisen, setSpeisen] = useState<SpeiseAnsicht[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [warenkorb, setWarenkorb] = useState<Warenkorbzeile[]>([]);
  const [phase, setPhase] = useState<Phase>("einkauf");
  const [review, setReview] = useState<ReviewAnsicht | null>(null);
  const [bestellung, setBestellung] = useState<BestellungAnsicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    let aktiv = true;
    speisekarte()
      .then((liste) => aktiv && setSpeisen(liste))
      .catch((e) => aktiv && setLadeFehler(fehlerText(e)));
    return () => {
      aktiv = false;
    };
  }, []);

  const hinzufuegen = (speise: SpeiseAnsicht) =>
    setWarenkorb((alt) => [...alt, neueZeile(speise)]);

  const entfernen = (schluessel: string) =>
    setWarenkorb((alt) => alt.filter((z) => z.schluessel !== schluessel));

  const weglassenUmschalten = (schluessel: string, zutat: string) =>
    setWarenkorb((alt) =>
      alt.map((z) =>
        z.schluessel !== schluessel
          ? z
          : {
              ...z,
              weglassen: z.weglassen.includes(zutat)
                ? z.weglassen.filter((n) => n !== zutat)
                : [...z.weglassen, zutat],
            },
      ),
    );

  const anfordern = async (eingabe: KassenEingabe) => {
    setFehler(null);
    setLaedt(true);
    try {
      const antwort = await otpAnfordern({ ...eingabe, zeilen: zuBestellzeilen(warenkorb) });
      setReview(antwort);
      setPhase("code");
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setLaedt(false);
    }
  };

  const bestaetigen = async (code: string) => {
    if (!review) return;
    setFehler(null);
    setLaedt(true);
    try {
      const antwort = await bestellungBestaetigen(review.challengeId, code);
      setBestellung(antwort);
      setPhase("bestaetigt");
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setLaedt(false);
    }
  };

  const zurueck = () => {
    setFehler(null);
    setReview(null);
    setPhase("einkauf");
  };

  const neu = () => {
    setWarenkorb([]);
    setReview(null);
    setBestellung(null);
    setFehler(null);
    setPhase("einkauf");
  };

  if (ladeFehler) {
    return <p className="fehler">Speisekarte konnte nicht geladen werden: {ladeFehler}</p>;
  }
  if (!speisen) {
    return <p className="hinweis">Speisekarte wird geladen …</p>;
  }

  if (phase === "bestaetigt" && bestellung) {
    return <Bestaetigung bestellung={bestellung} onNeu={neu} />;
  }

  if (phase === "code" && review) {
    return (
      <>
        {fehler && <p className="fehler">{fehler}</p>}
        <OtpBestaetigung
          review={review}
          laedt={laedt}
          onBestaetigen={bestaetigen}
          onZurueck={zurueck}
        />
      </>
    );
  }

  return (
    <div className="laden-raster">
      <section className="spalte">
        <h2>Speisekarte</h2>
        <Speisekarte speisen={speisen} onHinzufuegen={hinzufuegen} />
      </section>
      <section className="spalte">
        <h2>Warenkorb</h2>
        <WarenkorbListe
          warenkorb={warenkorb}
          onWeglassenUmschalten={weglassenUmschalten}
          onEntfernen={entfernen}
        />
        {warenkorb.length > 0 && (
          <>
            <h2>Kasse</h2>
            {fehler && <p className="fehler">{fehler}</p>}
            <Kasse laedt={laedt} onAnfordern={anfordern} />
          </>
        )}
      </section>
    </div>
  );
}

/** Wandelt einen Fehler in lesbaren deutschen Text; ApiFehler trägt ihn bereits. */
function fehlerText(e: unknown): string {
  if (e instanceof ApiFehler) return e.message;
  if (e instanceof Error) return e.message;
  return "Unerwarteter Fehler.";
}
